import type { ConnectorDataset } from '../types/api';
import type { AgentConfiguratorState } from '../types/agentConfigurator';
import type {
  CreatePipelineRequest,
  PipelineRun,
  PipelineTask,
  PipelineTaskRun,
  PipelineTaskRunStatus,
  PipelineTaskType,
} from '../types/pipeline';
import type { ReactionWeights } from '../types/reactionWeights';
import {
  buildOptimizationPolicy,
  type OptimizationPolicyUiState,
} from './optimizationPolicy';
import {
  DEFAULT_EVALUATION_MODE,
  DEFAULT_SELECTION_SCENARIO,
  deriveOptimizeByTokenFromSelectionScenario,
  normalizeEvaluationMode,
  resolveSelectionScenario,
} from '../types/optimizationJob';
import { getDefaultTrainingDateRange } from './defaultTrainingDateRange';
import {
  DEFAULT_FINE_TUNING_MODEL_PREFIX,
  preparePipelineFineTuningTaskConfig,
} from './fineTuningAgentPayload';
import {
  hasTrainingPeriodObject,
  normalizePipelinePeriodDaysForSave,
  sanitizeTrainingPeriodForSave,
} from './pipelineTrainingPeriod';

const DEFAULT_REACTION_WEIGHTS: ReactionWeights = {
  user: { like: 0.4, dislike: 0.3, propagation: 0.8 },
  admin: { like: 0.8, dislike: 0.5, propagation: 0.8 },
};

/** Max length for pipeline display name (form input + API payload). */
export const PIPELINE_NAME_MAX_LEN = 80;

export type PipelineTaskCatalogId = Exclude<PipelineTaskType, 'fine_tune'>;

export const PIPELINE_TASK_ORDER: PipelineTaskCatalogId[] = [
  'load_integration_datasets',
  'generate_vuser_datasets',
  'select_training_datasets',
  'config_tuning',
  'fine_tuning',
];

export interface PipelineTaskCatalogItem {
  type: PipelineTaskCatalogId;
  label: string;
  description: string;
  icon: string;
  accent: string;
}

export const PIPELINE_TASK_CATALOG: PipelineTaskCatalogItem[] = [
  {
    type: 'load_integration_datasets',
    label: 'Load Integration Datasets',
    description: 'Export dialogs and reactions from the integration into train/validation datasets.',
    icon: 'fa-database',
    accent: '#0099FA',
  },
  {
    type: 'generate_vuser_datasets',
    label: 'Generate VUser Datasets',
    description: 'Run virtual-user experiments and upload generated datasets.',
    icon: 'fa-users',
    accent: '#0099FA',
  },
  {
    type: 'select_training_datasets',
    label: 'Select Training Datasets',
    description: 'Pick which dataset types feed the next tuning and fine-tuning steps.',
    icon: 'fa-filter',
    accent: '#0072C6',
  },
  {
    type: 'config_tuning',
    label: 'Config Tuning',
    description: 'Optimize coordinator, workers and meta-prompts on selected datasets.',
    icon: 'fa-sliders-h',
    accent: '#FF8300',
  },
  {
    type: 'fine_tuning',
    label: 'Fine Tuning',
    description: 'Train fine-tuned models for selected agents on prepared datasets.',
    icon: 'fa-brain',
    accent: '#0072C6',
  },
];

export interface BuildPipelineTaskContext {
  integrationId: string;
  config: AgentConfiguratorState;
  optimizationPolicyUi: OptimizationPolicyUiState;
  virtualUserIds?: string[];
  virtualAdminIds?: string[];
  iterations?: number;
}

export function optimizationPayloadCoordinator(coordinator: AgentConfiguratorState['coordinator']) {
  return {
    name: coordinator.name,
    model: coordinator.model,
    temperature: coordinator.temperature,
    use_ltm: coordinator.use_ltm ?? false,
    use_rag: coordinator.use_rag ?? false,
    description: coordinator.description || '',
    prompt: coordinator.prompt || '',
  };
}

export function optimizationPayloadWorker(worker: AgentConfiguratorState['workers'][number]) {
  return {
    mode: worker.mode === 'asynchronous' ? 'asynchronous' : 'synchronous',
    name: worker.name,
    model: worker.model,
    temperature: worker.temperature,
    use_ltm: worker.use_ltm ?? false,
    use_rag: worker.use_rag ?? false,
    description: worker.description || '',
    prompt: worker.prompt || '',
  };
}

function buildFineTuningAgentsRaw(config: AgentConfiguratorState) {
  return [
    {
      agent_name: config.coordinator.name || 'Agent Coordinator',
      agent_id: config.coordinator.agent_id || config.coordinator.id,
      base_model: config.coordinator.model,
      is_trainable: config.coordinator.is_trainable !== false,
    },
    ...config.workers.map((worker, index) => ({
      agent_name: worker.name || `Worker ${index + 1}`,
      agent_id: worker.id,
      base_model: worker.model,
      is_trainable: worker.is_trainable !== false,
    })),
  ].filter((agent) => agent.is_trainable);
}

export function buildDefaultPipelineTask(
  type: PipelineTaskCatalogId,
  ctx: BuildPipelineTaskContext
): PipelineTask {
  const {
    integrationId,
    config,
    optimizationPolicyUi,
    virtualUserIds = [],
    virtualAdminIds = [],
    iterations = 2,
  } = ctx;
  const optimizationPolicy = buildOptimizationPolicy(config, optimizationPolicyUi);
  const approach = config.approach || 'MULTISTAGE-PACE';
  const metaPrompts = config.metaPrompts ? { ...config.metaPrompts } : undefined;
  const fineTuningAgentsRaw = buildFineTuningAgentsRaw(config);
  const fineTuningModelPrefix = DEFAULT_FINE_TUNING_MODEL_PREFIX;
  const fineTuningGroupByBaseModel = false;

  switch (type) {
    case 'load_integration_datasets':
      return {
        type,
        enabled: true,
        config: {
          integration_id: integrationId,
          training_period: getDefaultTrainingDateRange(),
          regime: 'overlap',
          regime_percent: 30,
          dataset_name_template: 'integration-dataset',
          train_ratio: 0.8,
          validation_ratio: 0.2,
          history_depth: 20,
          limit_history: true,
          comment: true,
          groundtruth: true,
          reactions: ['like', 'dislike'],
          reaction_weights: DEFAULT_REACTION_WEIGHTS,
        },
      };
    case 'generate_vuser_datasets':
      return {
        type,
        enabled: virtualUserIds.length > 0,
        config: {
          integration_id: integrationId,
          experiment_name: 'pipeline-vuser-dataset',
          virtual_user_ids: virtualUserIds,
          virtual_admin_ids: virtualAdminIds,
          dialog_count: 1,
          messages_per_dialog: 5,
          first_message: 'Hello, I need help with my request.',
          train_ratio: 0.8,
          validation_ratio: 0.2,
          history_depth: 20,
          limit_history: true,
          user_weights: {},
          reaction_weights: DEFAULT_REACTION_WEIGHTS,
        },
      };
    case 'select_training_datasets':
      return {
        type,
        enabled: true,
        config: {
          integration_id: integrationId,
          dataset_types: ['integration', 'vuser'],
          training_period: getDefaultTrainingDateRange(),
        },
      };
    case 'config_tuning':
      return {
        type,
        enabled: true,
        config: {
          mode: 'multi',
          integration_id: integrationId,
          approach,
          selection_scenario: DEFAULT_SELECTION_SCENARIO,
          evaluation_mode: DEFAULT_EVALUATION_MODE,
          evaluator_params: {},
          optimize_by_token: deriveOptimizeByTokenFromSelectionScenario(DEFAULT_SELECTION_SCENARIO),
          config: {
            iterations,
            coordinator: optimizationPayloadCoordinator(config.coordinator),
            workers: config.workers.map((worker) => optimizationPayloadWorker(worker)),
            metaPrompts,
          },
          optimization_policy: optimizationPolicy,
        },
      };
    case 'fine_tuning':
      return {
        type,
        enabled: fineTuningAgentsRaw.length > 0,
        config: preparePipelineFineTuningTaskConfig({
          integration_id: integrationId,
          group_by_base_model: fineTuningGroupByBaseModel,
          merge_train_validation: true,
          history_depth: 20,
          limit_history: true,
          model_name: fineTuningModelPrefix,
          agents: fineTuningAgentsRaw,
        }),
      };
    default:
      return { type, enabled: true, config: { integration_id: integrationId } };
  }
}

function mergeTaskConfig(
  type: PipelineTaskCatalogId,
  defaults: Record<string, unknown>,
  existing: Record<string, unknown>,
  integrationId: string
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...defaults, ...existing, integration_id: integrationId };
  if (type === 'config_tuning' || type === 'fine_tuning') {
    delete merged.dataset_ids;
  }
  return merged;
}

export function buildTasksFromTemplate(
  types: PipelineTaskCatalogId[],
  ctx: BuildPipelineTaskContext,
  existingTasks: PipelineTask[] = []
): PipelineTask[] {
  const byType = new Map(
    existingTasks.map((t) => [normalizePipelineTaskType(t.type), t] as const)
  );
  return types.map((type) => {
    const existing = byType.get(type);
    const defaults = buildDefaultPipelineTask(type, ctx);
    if (!existing) return defaults;
    return {
      type,
      enabled: existing.enabled,
      config: normalizePipelineTaskConfigForEditor(
        type,
        mergeTaskConfig(
          type,
          defaults.config as Record<string, unknown>,
          existing.config as Record<string, unknown>,
          ctx.integrationId
        )
      ),
    };
  });
}

function coercePipelineConfigBool(value: unknown, fallback: boolean): boolean {
  if (typeof value === 'boolean') return value;
  if (value === 1 || value === '1' || value === 'true') return true;
  if (value === 0 || value === '0' || value === 'false') return false;
  return fallback;
}

function normalizePipelineDatasetTypes(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => entry === 'integration' || entry === 'vuser');
  }
  if (typeof value === 'string' && value.trim()) {
    return value
      .split(/[,|]/)
      .map((entry) => entry.trim())
      .filter((entry): entry is 'integration' | 'vuser' => entry === 'integration' || entry === 'vuser');
  }
  return ['integration', 'vuser'];
}

function normalizePipelineReactions(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter((entry): entry is string => entry === 'like' || entry === 'dislike');
  }
  if (typeof value === 'string' && value.trim()) {
    return value
      .split(/[,|]/)
      .map((entry) => entry.trim())
      .filter((entry): entry is 'like' | 'dislike' => entry === 'like' || entry === 'dislike');
  }
  return ['like', 'dislike'];
}

/** Coerce API / legacy task config into editor-friendly booleans and arrays. */
export function normalizePipelineTaskConfigForEditor(
  type: PipelineTaskType,
  config: Record<string, unknown>
): Record<string, unknown> {
  const taskType = normalizePipelineTaskType(type);
  const normalized: Record<string, unknown> = { ...config };

  if ('comment' in normalized) {
    normalized.comment = coercePipelineConfigBool(normalized.comment, true);
  }
  if ('groundtruth' in normalized || 'ground_truth' in normalized) {
    normalized.groundtruth = coercePipelineConfigBool(
      normalized.groundtruth ?? normalized.ground_truth,
      true
    );
    delete normalized.ground_truth;
  }
  if ('limit_history' in normalized) {
    normalized.limit_history = coercePipelineConfigBool(normalized.limit_history, true);
  }
  if ('group_by_base_model' in normalized) {
    normalized.group_by_base_model = coercePipelineConfigBool(normalized.group_by_base_model, false);
  }
  if ('merge_train_validation' in normalized) {
    normalized.merge_train_validation = coercePipelineConfigBool(normalized.merge_train_validation, true);
  }

  if (taskType === 'select_training_datasets') {
    normalized.dataset_types = normalizePipelineDatasetTypes(normalized.dataset_types);
  }

  if (taskType === 'load_integration_datasets') {
    normalized.comment = coercePipelineConfigBool(normalized.comment, true);
    normalized.groundtruth = coercePipelineConfigBool(
      normalized.groundtruth ?? normalized.ground_truth,
      true
    );
    delete normalized.ground_truth;
    normalized.reactions = normalizePipelineReactions(normalized.reactions);
  }

  return normalized;
}

export function normalizePipelineTasksForEditor(tasks: PipelineTask[]): PipelineTask[] {
  return tasks.map((task) => ({
    ...task,
    type: normalizePipelineTaskType(task.type),
    config: normalizePipelineTaskConfigForEditor(task.type, { ...(task.config || {}) }),
  }));
}

export function sortTasksByPipelineOrder(tasks: PipelineTask[]): PipelineTask[] {
  const orderIndex = new Map(PIPELINE_TASK_ORDER.map((t, i) => [t, i]));
  return [...tasks].sort((a, b) => {
    const ia = orderIndex.get(normalizePipelineTaskType(a.type)) ?? 99;
    const ib = orderIndex.get(normalizePipelineTaskType(b.type)) ?? 99;
    return ia - ib;
  });
}

/** Remove manual dataset_ids when select_training_datasets precedes tuning steps. */
export function sanitizePipelineTasksForSave(
  tasks: PipelineTask[],
  integrationId?: string
): PipelineTask[] {
  const normalized = tasks.map((task) => ({
    type: normalizePipelineTaskType(task.type),
    enabled: Boolean(task.enabled),
    config: { ...task.config },
  }));

  const selectIndex = normalized.findIndex((t) => t.type === 'select_training_datasets');

  return normalized.map((task, index) => {
    let config = { ...task.config };

    if (integrationId) {
      config.integration_id = integrationId;
    }

    if (task.type === 'select_training_datasets' || task.type === 'load_integration_datasets') {
      const sanitizedPeriod = sanitizeTrainingPeriodForSave(config.training_period);
      if (sanitizedPeriod) {
        config.training_period = sanitizedPeriod;
        delete config.period_days;
      } else if (hasTrainingPeriodObject(config)) {
        delete config.training_period;
        config.period_days = normalizePipelinePeriodDaysForSave(config.period_days);
      } else {
        delete config.training_period;
        config.period_days = normalizePipelinePeriodDaysForSave(config.period_days);
      }
    }

    if (
      selectIndex >= 0 &&
      index > selectIndex &&
      task.enabled &&
      (task.type === 'config_tuning' || task.type === 'fine_tuning')
    ) {
      delete config.dataset_ids;
    }

    if (task.type === 'fine_tuning') {
      delete config.dataset_source;
      delete config.experiment_ids;
      if (typeof config.merge_train_validation !== 'boolean') {
        config.merge_train_validation = true;
      }
      if (typeof config.group_by_base_model !== 'boolean') {
        config.group_by_base_model = false;
      }
      config = preparePipelineFineTuningTaskConfig(config);
    }

    return { ...task, config };
  });
}

/** Rebuild tuning tasks from current configurator state before save. */
export function refreshPipelineTasksFromConfigurator(
  tasks: PipelineTask[],
  ctx: BuildPipelineTaskContext
): PipelineTask[] {
  return tasks.map((task) => {
    const type = normalizePipelineTaskType(task.type);
    if (type === 'config_tuning' || type === 'fine_tuning') {
      const fresh = buildDefaultPipelineTask(type, ctx);
      if (type === 'fine_tuning') {
        const mergedConfig: Record<string, unknown> = {
          ...fresh.config,
          ...(typeof task.config.model_name === 'string' && task.config.model_name.trim()
            ? { model_name: task.config.model_name.trim() }
            : {}),
          ...(typeof task.config.group_by_base_model === 'boolean'
            ? { group_by_base_model: task.config.group_by_base_model }
            : {}),
          ...(typeof task.config.merge_train_validation === 'boolean'
            ? { merge_train_validation: task.config.merge_train_validation }
            : {}),
          history_depth: task.config.history_depth ?? fresh.config.history_depth,
          limit_history: task.config.limit_history ?? fresh.config.limit_history,
        };
        return {
          ...fresh,
          enabled: task.enabled,
          config: preparePipelineFineTuningTaskConfig(mergedConfig),
        };
      }

      return {
        ...fresh,
        enabled: task.enabled,
        config: {
          ...fresh.config,
          ...(type === 'config_tuning'
            ? {
                ...(typeof task.config.approach === 'string' && task.config.approach.trim()
                  ? { approach: task.config.approach.trim() }
                  : {}),
                selection_scenario: resolveSelectionScenario(task.config),
                evaluation_mode: normalizeEvaluationMode(task.config.evaluation_mode),
                evaluator_params:
                  task.config.evaluator_params && typeof task.config.evaluator_params === 'object'
                    ? task.config.evaluator_params
                    : fresh.config.evaluator_params,
                optimize_by_token: deriveOptimizeByTokenFromSelectionScenario(
                  resolveSelectionScenario(task.config)
                ),
              }
            : {}),
        },
      };
    }
    return task;
  });
}

export function buildFullCyclePipelineRequest(
  params: BuildPipelineTaskContext & {
    name?: string;
    scheduleType?: 'hourly' | 'daily' | 'weekly' | 'monthly';
  }
): CreatePipelineRequest {
  const { name = 'Full AI Tuner cycle', scheduleType = 'daily', ...ctx } = params;
  const tasks = PIPELINE_TASK_ORDER.map((type) => buildDefaultPipelineTask(type, ctx));
  return {
    name,
    enabled: true,
    schedule_type: scheduleType,
    tasks,
  };
}

export function normalizePipelineTaskType(type: PipelineTaskType): PipelineTaskCatalogId {
  return type === 'fine_tune' ? 'fine_tuning' : type;
}

export function getTaskCatalogItem(type: PipelineTaskType): PipelineTaskCatalogItem | undefined {
  return PIPELINE_TASK_CATALOG.find((item) => item.type === normalizePipelineTaskType(type));
}

export function pipelineMatchesIntegration(
  pipeline: { tasks?: PipelineTask[] },
  integrationId: string
): boolean {
  const tasks = pipeline.tasks || [];
  if (tasks.length === 0) return true;
  return tasks.some((task) => {
    const configIntegrationId = task.config?.integration_id;
    return typeof configIntegrationId === 'string' && configIntegrationId === integrationId;
  });
}

export const PIPELINE_TASK_LABELS: Record<string, string> = Object.fromEntries(
  PIPELINE_TASK_CATALOG.map((item) => [item.type, item.label])
);
PIPELINE_TASK_LABELS.fine_tune = 'Fine Tuning (legacy)';

export function formatPipelineTaskType(type: string): string {
  return PIPELINE_TASK_LABELS[type] || type.replace(/_/g, ' ');
}

export function isPipelineRunActive(status: string | null | undefined): boolean {
  const s = String(status || '').toLowerCase();
  return s === 'queued' || s === 'running' || s === 'cancelling';
}

export function canCancelPipelineRun(status: string | null | undefined): boolean {
  const s = String(status || '').toLowerCase();
  return s === 'queued' || s === 'running';
}

function resolveSynthesizedTaskRunStatus(
  runStatus: string,
  taskIndex: number,
  currentTaskIndex: number
): PipelineTaskRunStatus {
  if (runStatus === 'succeeded') return 'succeeded';
  if (runStatus === 'cancelled' || runStatus === 'cancelling') {
    if (taskIndex < currentTaskIndex) return 'succeeded';
    if (taskIndex === currentTaskIndex) return 'cancelled';
    return 'skipped';
  }
  if (runStatus === 'failed') {
    if (taskIndex < currentTaskIndex) return 'succeeded';
    if (taskIndex === currentTaskIndex) return 'failed';
    return 'skipped';
  }
  if (runStatus === 'partially_succeeded') {
    if (taskIndex < currentTaskIndex) return 'succeeded';
    if (taskIndex === currentTaskIndex) return 'failed';
    return 'skipped';
  }
  return 'skipped';
}

function resolveSynthesizedTaskProgress(
  runStatus: string,
  taskIndex: number,
  currentTaskIndex: number,
  runProgressPercent?: number
): number | undefined {
  if (runStatus === 'succeeded') return 100;
  if (taskIndex < currentTaskIndex) return 100;
  if (taskIndex > currentTaskIndex) return 0;
  return typeof runProgressPercent === 'number' ? runProgressPercent : undefined;
}

/** Task rows for the run panel when the API omits `task_runs` on a finished run. */
export function resolvePipelineRunDisplayTaskRuns(
  run: Pick<PipelineRun, 'status' | 'task_runs' | 'current_task_index' | 'progress_percent'>,
  pipelineTasks?: PipelineTask[]
): PipelineTaskRun[] {
  const existing = run.task_runs ?? [];
  if (existing.length > 0) return existing;
  if (isPipelineRunActive(run.status)) return [];

  const tasks = (pipelineTasks ?? []).filter((task) => task.enabled !== false);
  if (tasks.length === 0) return [];

  const currentTaskIndex = Math.max(0, run.current_task_index ?? 0);
  const runStatus = String(run.status || '').toLowerCase();

  return tasks.map((task, index) => ({
    type: task.type,
    status: resolveSynthesizedTaskRunStatus(runStatus, index, currentTaskIndex),
    progress_percent: resolveSynthesizedTaskProgress(
      runStatus,
      index,
      currentTaskIndex,
      run.progress_percent
    ),
  }));
}

export function getEmptyPipelineRunTaskRunsMessage(status: string | null | undefined): string {
  const runStatus = String(status || '').toLowerCase();
  if (runStatus === 'cancelled' || runStatus === 'cancelling') {
    return 'This run was cancelled before task execution details were recorded.';
  }
  if (runStatus === 'failed') {
    return 'No task execution details are available for this failed run.';
  }
  if (runStatus === 'succeeded' || runStatus === 'partially_succeeded') {
    return 'No task execution details are available for this run.';
  }
  return 'Waiting for task details…';
}

export function pipelineStatusColor(status: string | null | undefined): string {
  const s = String(status || '').toLowerCase();
  if (s === 'succeeded') return '#0072C6';
  if (s === 'partially_succeeded') return '#B85400';
  if (s === 'failed') return '#FF8300';
  if (s === 'running') return '#0099FA';
  if (s === 'queued') return '#5B7893';
  if (s === 'cancelled' || s === 'cancelling') return '#7F9AB3';
  if (s === 'skipped') return '#7F9AB3';
  return '#5B7893';
}

function datasetRowSource(row: ConnectorDataset): string {
  const meta = row.metadata;
  if (meta && typeof meta === 'object' && !Array.isArray(meta)) {
    const source = (meta as Record<string, unknown>).source;
    if (typeof source === 'string' && source.trim()) return source.trim().toLowerCase();
  }
  return '';
}

function datasetRowPeriod(row: ConnectorDataset): { from: number; to: number } | null {
  const meta = row.metadata;
  const tp =
    meta && typeof meta === 'object' && !Array.isArray(meta)
      ? (meta as Record<string, unknown>).training_period
      : undefined;
  if (tp && typeof tp === 'object' && !Array.isArray(tp)) {
    const from = (tp as { from?: string }).from;
    const to = (tp as { to?: string }).to;
    if (from && to) {
      const fromMs = new Date(from).getTime();
      const toMs = new Date(to).getTime();
      if (!Number.isNaN(fromMs) && !Number.isNaN(toMs)) return { from: fromMs, to: toMs };
    }
  }
  const created = row.created_at || row.createdAt;
  if (created) {
    const ms = new Date(String(created)).getTime();
    if (!Number.isNaN(ms)) return { from: ms, to: ms };
  }
  return null;
}

function sourceMatchesDatasetType(source: string, datasetType: string): boolean {
  if (datasetType === 'integration') {
    return source === 'integration' || source === '';
  }
  if (datasetType === 'vuser') {
    return source === 'vuser' || source === 'virtual_user';
  }
  return source === datasetType;
}

/** Registry preview rows matching select_training_datasets filters. */
export function filterRegistryDatasetsForSelectStep(
  datasets: ConnectorDataset[],
  datasetTypes: string[],
  opts: { periodDays?: number; trainingPeriod?: { from?: string; to?: string } }
): ConnectorDataset[] {
  const types = datasetTypes.length > 0 ? datasetTypes : ['integration', 'vuser'];

  let rangeFrom: number | null = null;
  let rangeTo: number | null = null;
  if (opts.trainingPeriod?.from && opts.trainingPeriod?.to) {
    rangeFrom = new Date(opts.trainingPeriod.from).getTime();
    rangeTo = new Date(opts.trainingPeriod.to).getTime();
  } else if (opts.periodDays != null && Number.isFinite(opts.periodDays)) {
    rangeTo = Date.now();
    rangeFrom = rangeTo - opts.periodDays * 24 * 60 * 60 * 1000;
  }

  return datasets.filter((row) => {
    const source = datasetRowSource(row);
    const typeMatch = types.some((t) => sourceMatchesDatasetType(source, t));
    if (!typeMatch) return false;

    if (rangeFrom == null || rangeTo == null || Number.isNaN(rangeFrom) || Number.isNaN(rangeTo)) {
      return true;
    }

    const period = datasetRowPeriod(row);
    if (!period) return true;
    return period.to >= rangeFrom && period.from <= rangeTo;
  });
}

export function normalizePipelineUpdateBody(
  form: CreatePipelineRequest,
  integrationId?: string
): CreatePipelineRequest {
  const tasks = sanitizePipelineTasksForSave(sortTasksByPipelineOrder(form.tasks), integrationId);
  return {
    name: form.name.trim().slice(0, PIPELINE_NAME_MAX_LEN),
    enabled: Boolean(form.enabled),
    schedule_type: form.schedule_type ?? null,
    tasks: tasks.map(({ type, enabled, config }) => ({
      type: normalizePipelineTaskType(type),
      enabled: Boolean(enabled),
      config,
    })),
  };
}
